import styled from '@emotion/styled';
import { useIsMobile } from '@/common/hooks';
import CheckIcon from '@mui/icons-material/Check';

import { designSystem } from '@/styles/theme';

interface Step {
  step: number;
  value: string;
}

interface StepUIProps {
  stepData: Step[];
  target: number;
  width?: string;
}

const StepperContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${props => props.isMobile ? '100%' : '480px'};
  margin: 24px auto;
  /* 폰은 감싼 화면이 이미 좌우 16px — 한 번 더 주면 320 폭에서 칸이 좁아져 글자가 붙는다 */
  padding: 0;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

const StepColumn = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  /* 이웃 칸 글자 사이가 붙지 않게 칸마다 좌우 2px(사이 4px 이상) */
  padding: 0 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Connector = styled.div<{ filled: boolean }>`
  position: absolute;
  top: 13px;
  left: 50%;
  width: 100%;
  height: 3px;
  background: ${props => props.filled ? designSystem.colors.primary : '#e5e7eb'};
  border-radius: 2px;
  transition: background 0.3s ease;
  z-index: 0;
`;

const StepIndicator = styled.div<{ status: 'completed' | 'current' | 'inactive' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;

  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: ${designSystem.colors.primary};
          color: #fff;
        `;
      case 'current':
        return `
          background: #fff;
          color: ${designSystem.colors.primary};
          border: 2px solid ${designSystem.colors.primary};
        `;
      case 'inactive':
        return `
          background: #e5e7eb;
          color: #9ca3af;
        `;
    }
  }}
`;

const StepLabel = styled.div<{ status: 'completed' | 'current' | 'inactive'; isMobile: boolean }>`
  margin-top: 8px;
  font-size: ${props => props.isMobile ? '12px' : '13px'};
  font-weight: ${props => props.status === 'current' ? '600' : '400'};
  color: ${props => {
    switch (props.status) {
      case 'completed':
        return designSystem.colors.primary;
      case 'current':
        return '#1f2937';
      case 'inactive':
        return '#9ca3af';
    }
  }};
  text-align: center;
  line-height: 1.35;
  /* 좁은 폭(320~360)에서는 낱말 단위로 줄을 바꾼다 — 한 줄로 고정하면 옆 칸 글자와 겹친다 */
  white-space: normal;
  word-break: keep-all;
  max-width: 100%;
`;

function Stepper({ stepData, target }: StepUIProps) {
  const isMobile = useIsMobile();

  const getStatus = (step: number): 'completed' | 'current' | 'inactive' => {
    if (step < target) return 'completed';
    if (step === target) return 'current';
    return 'inactive';
  };

  return (
    <StepperContainer isMobile={isMobile}>
      <ProgressBarWrapper>
        {stepData.map((item, index) => (
          <StepColumn key={item.step}>
            {index < stepData.length - 1 && (
              <Connector filled={item.step < target} />
            )}
            <StepIndicator status={getStatus(item.step)}>
              {item.step < target ? (
                <CheckIcon style={{ fontSize: '16px' }} />
              ) : (
                item.step
              )}
            </StepIndicator>
            <StepLabel status={getStatus(item.step)} isMobile={isMobile}>
              {item.value}
            </StepLabel>
          </StepColumn>
        ))}
      </ProgressBarWrapper>
    </StepperContainer>
  );
}

export default Stepper;
